/**
 * Research Service API Client
 * Provides type-safe access to research-service REST endpoints
 */

import type {
  EntityType,
  DimensionType,
  LimitOption,
  ResearchResponse,
  GapResponse,
  ResearchRequestParams,
  GapRequestParams,
} from '../types/research-service';

// ============================================================================
// Configuration
// ============================================================================

const RESEARCH_SERVICE_URL =
  import.meta.env.VITE_RESEARCH_SERVICE_URL ||
  'http://localhost:4010';

const DEFAULT_TIMEOUT = 30000; // 30 seconds

// ============================================================================
// Error Handling
// ============================================================================

export class ResearchServiceError extends Error {
  constructor(
    message: string,
    public status?: number,
    public endpoint?: string
  ) {
    super(message);
    this.name = 'ResearchServiceError';
  }
}

// ============================================================================
// HTTP Client
// ============================================================================

async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(id);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new ResearchServiceError(
        `Research service error: ${errorText}`,
        response.status,
        url
      );
    }

    return response;
  } catch (error) {
    clearTimeout(id);

    if (error instanceof ResearchServiceError) {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new ResearchServiceError(
        `Request timeout after ${timeout}ms`,
        408,
        url
      );
    }

    throw new ResearchServiceError(
      `Network error: ${error instanceof Error ? error.message : 'Unknown'}`,
      undefined,
      url
    );
  }
}

// ============================================================================
// Core API Methods
// ============================================================================

/**
 * Generic method to fetch research data
 */
async function fetchResearchData(
  entity: EntityType,
  params: ResearchRequestParams
): Promise<ResearchResponse> {
  const { dimension, value, limit = 10 } = params;
  const url = `${RESEARCH_SERVICE_URL}/${entity}/${dimension}/${encodeURIComponent(value)}?limit=${limit}`;

  const response = await fetchWithTimeout(url);
  return response.json();
}

/**
 * Generic method to fetch gap analysis
 */
async function fetchGapAnalysis(
  gapType: 'topics' | 'questions',
  params: GapRequestParams
): Promise<GapResponse> {
  const { dimension, value, customerId } = params;
  const url = `${RESEARCH_SERVICE_URL}/gaps/${gapType}/${dimension}/${encodeURIComponent(value)}/${customerId}`;

  const response = await fetchWithTimeout(url);
  return response.json();
}

// ============================================================================
// Public API
// ============================================================================

export const researchService = {
  /**
   * Health check endpoint
   */
  async health(): Promise<{ status: string; timestamp: string }> {
    const url = `${RESEARCH_SERVICE_URL}/health`;
    const response = await fetchWithTimeout(url);
    return response.json();
  },

  // -------------------------------------------------------------------------
  // Topics
  // -------------------------------------------------------------------------

  async getTopics(params: ResearchRequestParams): Promise<ResearchResponse> {
    return fetchResearchData('topics', params);
  },

  async getTopicGaps(params: GapRequestParams): Promise<GapResponse> {
    return fetchGapAnalysis('topics', params);
  },

  // -------------------------------------------------------------------------
  // Opinions
  // -------------------------------------------------------------------------

  async getOpinions(params: ResearchRequestParams): Promise<ResearchResponse> {
    return fetchResearchData('opinions', params);
  },

  // -------------------------------------------------------------------------
  // Questions
  // -------------------------------------------------------------------------

  async getQuestions(params: ResearchRequestParams): Promise<ResearchResponse> {
    return fetchResearchData('questions', params);
  },

  async getQuestionGaps(params: GapRequestParams): Promise<GapResponse> {
    return fetchGapAnalysis('questions', params);
  },

  // -------------------------------------------------------------------------
  // Categories
  // -------------------------------------------------------------------------

  async getCategories(params: ResearchRequestParams): Promise<ResearchResponse> {
    return fetchResearchData('categories', params);
  },

  // -------------------------------------------------------------------------
  // Products
  // -------------------------------------------------------------------------

  async getProducts(params: ResearchRequestParams): Promise<ResearchResponse> {
    return fetchResearchData('products', params);
  },

  // -------------------------------------------------------------------------
  // Threads
  // -------------------------------------------------------------------------

  async getThreads(params: ResearchRequestParams): Promise<ResearchResponse> {
    return fetchResearchData('threads', params);
  },
};

export default researchService;
